import { BTN_JUMP, BTN_LEFT, BTN_RIGHT, PLAYER_H, PLAYER_W, ROWS, TILE } from '../shared/constants.ts';
import { T_SOLID, T_SPIKE } from '../shared/level.ts';
import { Rng } from '../shared/rng.ts';
import type { Sim } from '../shared/sim.ts';

const ACTIONS = [BTN_RIGHT, BTN_RIGHT | BTN_JUMP, 0, BTN_JUMP, BTN_LEFT, BTN_LEFT | BTN_JUMP];
const HOLDS = [4, 10, 18];

/**
 * Brute-force look-ahead player: tries each (action, hold duration) on a clone
 * of the sim and keeps the one that survives longest. `skill` (0..1) shortens
 * the look-ahead, slows reactions and adds blunders, so bots die eventually.
 */
export class Brain {
  readonly skill: number;
  /** Getting sloppy: a lot more blunders (a house player wrapping up because someone real is waiting). */
  tired = false;
  readonly horizon: number;
  private readonly reaction: number;
  private readonly rng: Rng;
  private plan: number[] = [];

  constructor(skill: number, seed: number) {
    this.skill = skill;
    this.rng = new Rng(seed);
    this.horizon = 16 + Math.floor(skill * 50);
    this.reaction = 2 + Math.floor((1 - skill) * 8);
  }

  next(sim: Sim): number {
    if (this.plan.length === 0) this.replan(sim);
    return this.plan.shift()!;
  }

  private replan(sim: Sim): void {
    if (this.rng.chance(this.tired ? 0.2 : (1 - this.skill) * 0.04)) {
      this.plan = new Array(this.rng.int(6, 20)).fill(this.rng.pick(ACTIONS));
      return;
    }
    let best = -Infinity;
    let bestAction = BTN_RIGHT;
    for (const a of ACTIONS) {
      for (const h of HOLDS) {
        const s = sim.clone();
        let survived = 0;
        for (let i = 0; i < this.horizon; i++) {
          s.step(i < h ? a : reflex(s));
          if (!s.alive) break;
          survived++;
        }
        const score = survived * 1e6 + (s.x - s.scrollX) + (a & BTN_RIGHT ? 1 : 0);
        if (score > best) {
          best = score;
          bestAction = a;
        }
      }
    }
    this.plan = new Array(this.reaction).fill(bestAction);
  }
}

/** Rollout continuation: run right, jump when something nasty is just ahead. */
function reflex(s: Sim): number {
  const held = (s.prevInput & BTN_JUMP) !== 0;
  if (!s.onGround) return held && s.vy < 0 ? BTN_RIGHT | BTN_JUMP : BTN_RIGHT;
  const c = Math.floor((s.x + PLAYER_W + Math.max(s.vx, 16) * 8) / TILE);
  const body = Math.floor((s.y + PLAYER_H - 1) / TILE);
  let pit = true;
  for (let r = body + 1; r < ROWS; r++) if (s.level.tile(c, r) === T_SOLID) pit = false;
  const t = s.level.tile(c, body);
  const danger = pit || t === T_SPIKE || t === T_SOLID || s.level.tile(c, body - 1) === T_SOLID;
  return danger && !held ? BTN_RIGHT | BTN_JUMP : BTN_RIGHT;
}
